// MobileSeries — aba SÉRIES da superfície MOBILE (touch). Parede A5.
//
// Espelha a MobileMovies: chips de categoria + grade vertical. Reusa a camada
// de dados da SeriesScreen da TV: api.getSeriesCategories (chips),
// api.getRecentSeries ("Todos") e api.getSeries(catId) (categoria). Keys
// scopadas via useProviderQueryKey. Tap num card → tela de DETALHE da série.

import { useTranslation } from '@app-iptv/i18n'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { api, ApiError } from '../api'
import { useProviderQueryKey } from '../hooks/useScopedQueryKey'

import { buildCatalogChips } from './mobile-catalog'
import styles from './MobileCatalog.module.css'
import { MobileChips } from './MobileChips'
import { MobileGrid } from './MobileGrid'
import type { MobileHomeItem } from './MobileHome'
import { MobileEmpty, MobileLoading, MobileNoSource } from './MobileStates'
import { MobileVodCard } from './MobileVodCard'

const PAGE_SIZE = 120

function isNoProvider(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'no_provider'
}

export interface MobileSeriesProps {
  onOpenItem: (item: MobileHomeItem) => void
  onConfigureSource?: (() => void) | undefined
}

export function MobileSeries({ onOpenItem, onConfigureSource }: MobileSeriesProps): JSX.Element {
  const { t } = useTranslation()
  const [activeCat, setActiveCat] = useState<string | null>(null)

  const categoriesQ = useQuery({
    queryKey: useProviderQueryKey(['series-categories']),
    queryFn: () => api.getSeriesCategories(),
    staleTime: 30 * 60 * 1000,
    retry: (count, err) => !isNoProvider(err) && count < 2,
  })

  const listQ = useQuery({
    queryKey: useProviderQueryKey(['mobile-series-list', activeCat ?? 'all', PAGE_SIZE]),
    queryFn: () =>
      activeCat ? api.getSeries(activeCat, 1, PAGE_SIZE) : api.getRecentSeries(PAGE_SIZE),
    staleTime: 10 * 60 * 1000,
    retry: (count, err) => !isNoProvider(err) && count < 2,
  })

  const chips = useMemo(
    () => buildCatalogChips(categoriesQ.data ?? [], t('common.all')),
    [categoriesQ.data, t],
  )
  const items = useMemo(() => listQ.data ?? [], [listQ.data])

  const noProvider = isNoProvider(categoriesQ.error) || isNoProvider(listQ.error)
  if (noProvider) {
    return <MobileNoSource onConfigureSource={onConfigureSource} />
  }

  return (
    <div className={styles.screen} data-screen="series">
      <MobileChips
        chips={chips}
        activeId={activeCat}
        onSelect={setActiveCat}
        ariaLabel={t('nav.series')}
      />

      {listQ.isLoading && items.length === 0 ? (
        <MobileLoading />
      ) : items.length === 0 ? (
        <MobileEmpty title={t('empty.noCategoryTitle')} text={t('empty.noCategoryDesc')} />
      ) : (
        <MobileGrid gridId="series">
          {items.map((s) => (
            <MobileVodCard
              key={s.id}
              title={s.name}
              poster={s.poster}
              year={s.year}
              kind="series"
              onPress={() => onOpenItem({ id: s.id, kind: 'series', title: s.name, poster: s.poster })}
            />
          ))}
        </MobileGrid>
      )}
    </div>
  )
}
